import { iconDocGenericoPreco, iconFinGrafico } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import JSZip from 'jszip';
import React, { useCallback, useId, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
} from 'recharts';

import { DashboardPaymentSummaryTable, Dropdown, SystemMessage, useSystemContainer } from '../../../../components';
import { useVidaDashboardPayment } from '../../../../hooks';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { useCompanyStore } from '../../../../store/companyStore';
import type { DashboardPaymentSummaryItem, DashboardViewType, ApoliceSubestipulante } from '../../../../types';
import {
  getSelectedPolicyIdentEstipulanteApol,
  hasSelectedCompanyPolicyNumber,
} from '../../../../types/companyType';
import { normalizePapelToRoleHeader } from '../../../../utils/resolveEffectiveRole';
import { runTabMountSequence } from '../../../../utils/tabLoadTiming';

type UserRoleValue = 'CORRETOR' | 'ESTIPULANTE' | 'BACKOFFICE' | 'SUB-ESTIPULANTE';
const ROLE_CORRETOR: UserRoleValue = 'CORRETOR';
const ROLE_ESTIPULANTE: UserRoleValue = 'ESTIPULANTE';
const ROLE_BACKOFFICE: UserRoleValue = 'BACKOFFICE';

const EMPTY_APOLICE_SUB_LIST: ApoliceSubestipulante[] = [];

const DashboardPaymentTab: React.FC = () => {
  const { setLoading } = useSystemContainer();
  const { showMessage, message, type, show } = useSystemMessage();
  const mainContentId = useId();
  const messageId = useId();
  const chartId = useId();
  const summarySectionId = useId();
  const searchInputId = useId();

  const userRole = useCompanyStore((state) => {
    const policyRole = state.selectedCompany?.policySelection?.papelDescricao?.trim();
    if (!policyRole) return undefined;
    return normalizePapelToRoleHeader(policyRole) as UserRoleValue;
  });
  const viewType: DashboardViewType = useMemo(() => {
    const roleToViewType: Record<UserRoleValue, DashboardViewType> = {
      [ROLE_CORRETOR]: 'corretora',
      [ROLE_ESTIPULANTE]: 'estipulante',
      [ROLE_BACKOFFICE]: 'apolice',
      'SUB-ESTIPULANTE': 'estipulante',
    };
    if (!userRole) return 'corretora';
    return roleToViewType[userRole] || 'corretora';
  }, [userRole]);

  const selectedCompany = useCompanyStore((s) => s.selectedCompany);
  const policySelection = selectedCompany?.policySelection;
  const subestipulantes = selectedCompany?.subestipulantes ?? [];

  const policyIdentEstipulante = useMemo(
    () => getSelectedPolicyIdentEstipulanteApol(selectedCompany),
    [selectedCompany]
  );

  const apoliceSubList: ApoliceSubestipulante[] = useMemo(() => {
    const nroApolice = policySelection?.numeroApolice?.trim();
    if (!nroApolice || subestipulantes.length === 0) {
      return EMPTY_APOLICE_SUB_LIST;
    }

    return subestipulantes
      .filter((item) => item.identSubestipulanteOut > 0)
      .map((item) => ({
        nroApolice,
        cdSubEstipulante: String(item.identSubestipulanteOut),
      }));
  }, [policySelection?.numeroApolice, subestipulantes]);

  // Map viewType to tpVisao numeric string (according to dashboardfaturamento.txt)
  // "apolice" → "1" (resumo-apolice, grafico-faturamento)
  // "corretora" / "estipulante" → "2" (resumo-corretora-estipulante)
  const tpVisao = useMemo(() => {
    if (viewType === 'apolice') {
      return '1';
    }
    // broker or policyholder → "2"
    return '2';
  }, [viewType]);

  // Body: idCorretor / idEstipulante (resumo-corretora-estipulante e resumo-apolice)
  const idCorretor = useMemo(() => 0, []);
  const idEstipulante = useMemo(() => {
    const isEstipulanteProfile =
      userRole === ROLE_ESTIPULANTE || userRole === 'SUB-ESTIPULANTE';
    if (isEstipulanteProfile && policyIdentEstipulante != null) {
      return policyIdentEstipulante;
    }
    return 0;
  }, [userRole, policyIdentEstipulante]);

  const hookParams = useMemo(
    () => ({ tpVisao, idCorretor, idEstipulante, apoliceSubList }),
    [tpVisao, idCorretor, idEstipulante, apoliceSubList]
  );

  const {
    isLoading,
    totalWebAmount,
    totalManualAmount,
    chartData,
    formatCurrency,
    formatPercent,
    summaryTableData,
    loadSummaryData,
    currentPage,
    totalPages,
    totalElements,
    relatorioDownloadRetorno,
    getDashboardPaymentInvoiceTypeGraphic,
    getDashboardPaymentSummary,
  } = useVidaDashboardPayment(hookParams);

  // Search and sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('');
  const [tabMountBusy, setTabMountBusy] = useState(false);

  // Initial load with error handling: 1s after mount before requests; loading visible ≥ 2s total
  React.useEffect(() => {
    if (!hasSelectedCompanyPolicyNumber(selectedCompany)) return;
    if (!hookParams?.tpVisao) return;
    const requiresApoliceSubList =
      userRole === ROLE_ESTIPULANTE || userRole === 'SUB-ESTIPULANTE';
    if (requiresApoliceSubList && (hookParams.apoliceSubList?.length ?? 0) === 0) {
      return;
    }

    let cancelled = false;

    const loadInitialData = async () => {
      await runTabMountSequence(async () => {
        try {
          if (userRole === ROLE_BACKOFFICE) {
            await getDashboardPaymentInvoiceTypeGraphic({
              tpVisao: hookParams.tpVisao,
              apoliceSubList: hookParams.apoliceSubList ?? [],
            });
          }
        } catch (error) {
          console.error('Erro ao carregar dados do dashboard:', error);
          showMessage('Não foi possível carregar os dados do dashboard. Tente novamente.', 'error');
        }

        try {
          await getDashboardPaymentSummary({
            tpVisao: hookParams.tpVisao,
            idCorretor: hookParams.idCorretor,
            idEstipulante: hookParams.idEstipulante,
            apoliceSubList: hookParams.apoliceSubList ?? [],
            page: 0,
            size: 6,
          });
        } catch (error) {
          console.error('Erro ao carregar dados do resumo:', error);
          showMessage('Não foi possível carregar os dados do resumo. Tente novamente.', 'error');
        }
      });
    };

    (async () => {
      setTabMountBusy(true);
      try {
        await loadInitialData();
      } finally {
        if (!cancelled) setTabMountBusy(false);
      }
    })();

    return () => {
      cancelled = true;
      setTabMountBusy(false);
    };
  }, [
    selectedCompany,
    hookParams?.tpVisao,
    hookParams?.apoliceSubList,
    hookParams?.idCorretor,
    hookParams?.idEstipulante,
    userRole,
    getDashboardPaymentInvoiceTypeGraphic,
    getDashboardPaymentSummary,
    showMessage,
  ]);

  // Filter and sort data (client-side filtering for search and sort)
  const filteredAndSortedData: DashboardPaymentSummaryItem[] = useMemo(() => {
    let filtered = summaryTableData;

    // Apply search filter by contract number
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) =>
        (item.contractNumber ?? '').toLowerCase().includes(term)
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'percentPending':
            return a.percentPending - b.percentPending;
          case 'percentPending-desc':
            return b.percentPending - a.percentPending;
          case 'percentIssued':
            return a.percentIssued - b.percentIssued;
          case 'percentIssued-desc':
            return b.percentIssued - a.percentIssued;
          case 'valuePending':
            return a.valuePending - b.valuePending;
          case 'valuePending-desc':
            return b.valuePending - a.valuePending;
          case 'valueIssued':
            return a.valueIssued - b.valueIssued;
          case 'valueIssued-desc':
            return b.valueIssued - a.valueIssued;
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [summaryTableData, searchTerm, sortBy]);

  // Use summaryTableData directly (pagination is handled by backend)
  const paginatedSummaryData = filteredAndSortedData;

  // Convert backend page (0-based) to UI page (1-based) for Pagination component
  const uiCurrentPage = currentPage + 1;

  const handlePageChange = async (page: number) => {
    // Convert UI page (1-based) to backend page (0-based)
    const backendPage = page - 1;
    try {
      await loadSummaryData({
        page: backendPage,
      });
    } catch (error) {
      console.error('Erro ao carregar dados da página:', error);
      showMessage('Não foi possível carregar os dados. Tente novamente.', 'error');
    }
  };

  // Custom tooltip formatter - shows only the bar being hovered
  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // When hovering over a specific bar, Recharts may include both bars in payload
      // We need to find which one is actually being hovered by checking which has a valid value
      // The hovered bar will have its value displayed, others might be null/undefined
      const hoveredEntry = payload.find((entry: any) =>
        entry.value != null && entry.value !== undefined && entry.value > 0
      ) || payload[0];

      if (!hoveredEntry || !hoveredEntry.payload) return null;

      const data = hoveredEntry.payload;
      const isWeb = hoveredEntry.dataKey === 'Web';
      const color = isWeb ? '#AA0827' : '#00518E';
      const name = isWeb ? 'Web' : 'Manual';
      const valor = isWeb ? data.webValor : data.manualValor;
      const percentage = isWeb ? data.webPercentage : data.manualPercentage;
      const count = isWeb ? data.webCount : data.manualCount;

      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="text-sm font-semibold mb-2" style={{ color }}>
            {name}
          </p>
          <p className="text-xs" style={{ color }}>
            Valor: {formatCurrency(valor)}
          </p>
          <p className="text-xs" style={{ color }}>
            Percentual: {formatPercent(percentage)}
          </p>
          <p className="text-xs" style={{ color }}>
            Quantidade: {count}
          </p>
        </div>
      );
    }
    return null;
  };

  // Sync loading state with SystemContainer
  React.useEffect(() => {
    setLoading(tabMountBusy || isLoading);
  }, [tabMountBusy, isLoading, setLoading]);

  // Export: unzip base64 from graphic response and trigger download
  const handleExport = useCallback(async () => {
    if (!relatorioDownloadRetorno?.arquivo) {
      showMessage('Relatório não disponível para download.', 'warning');
      return;
    }
    try {
      const binaryString = atob(relatorioDownloadRetorno.arquivo);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/zip' });
      const zip = await JSZip.loadAsync(blob);
      const fileNames = Object.keys(zip.files).filter((name) => !zip.files[name].dir);
      if (fileNames.length === 0) {
        showMessage('Nenhum arquivo encontrado no relatório.', 'warning');
        return;
      }
      for (const fileName of fileNames) {
        const entry = zip.files[fileName];
        const content = await entry.async('blob');
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.includes('/') ? fileName.replace(/.*\//, '') : fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
      showMessage(
        fileNames.length === 1
          ? 'Download do relatório iniciado.'
          : `${fileNames.length} arquivo(s) do relatório baixado(s).`,
        'success'
      );
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      showMessage('Erro ao fazer download do relatório. Tente novamente.', 'error');
    }
  }, [relatorioDownloadRetorno, showMessage]);


  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={tabMountBusy || isLoading}
      aria-label="Dashboard de faturamento"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}

      {/* Header */}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <BsdsIcon icon={iconFinGrafico} className="w-4 h-4" aria-hidden="true" />
        <h6 className="bsds-ml-8 font-semibold">Dashboard de Faturamento</h6>
      </div>

      {/* Info Cards - Two cards on top */}
      <div
        className="flex justify-center bsds-mt-24"
        role="region"
        aria-label="Resumo de valores emitidos"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8 items-stretch w-full justify-items-center md:justify-items-center max-w-[600px]">

          {/* Manual Amount Card - First (Blue) */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full" platform="web" role="region" aria-label="Valor emitido manualmente">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Valor Emitido Manual</span>
                <span className="text-md font-bold text-secondary-600" aria-label={`Valor total emitido manualmente: ${formatCurrency(totalManualAmount)}`}>
                  {formatCurrency(totalManualAmount)}
                </span>
              </div>
              <div className="w-5 h-5 rounded bg-secondary-500" aria-hidden="true" />
            </div>
          </BsdsCard>

          {/* Web Amount Card - Second (Red) */}
          <BsdsCard className="p-4 w-full sm:w-full md:w-full" platform="web" role="region" aria-label="Valor emitido via web">
            <div className="flex justify-between items-center h-full">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-text-color mb-2 uppercase">Valor Emitido Web</span>
                <span className="text-md font-bold text-primary-base" aria-label={`Valor total emitido via web: ${formatCurrency(totalWebAmount)}`}>
                  {formatCurrency(totalWebAmount)}
                </span>
              </div>
              <div className="w-5 h-5 rounded bg-primary-base" aria-hidden="true" />
            </div>
          </BsdsCard>
        </div>
      </div>

      {/* Chart - Web vs Manual comparison */}
      <BsdsCard
        id={chartId}
        className="bsds-mt-32"
        role="region"
        aria-label="Gráfico de valores emitidos por meio de faturamento"
      >
        <div className="bsds-p-4 shadow-xs">
          {chartData && chartData.length > 0 ? (
            <div className="w-full" role="img" aria-label="Gráfico de barras mostrando valores emitidos via web e manualmente">
              <style>{`
                .recharts-active-bar {
                  opacity: 0.8 !important;
                }
                .recharts-bar-rectangle {
                  transition: opacity 0.2s ease;
                }
                .recharts-bar-rectangle:hover {
                  opacity: 0.85 !important;
                }
              `}
              </style>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  barGap={20}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#d0d4db" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    content={customTooltip}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                    shared={false}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '8px' }}
                    formatter={(value) => value}
                  />
                  {/* Manual first (Blue) */}
                  <Bar
                    dataKey="Manual"
                    fill="#00518E"
                    name="Manual"
                    radius={[4, 4, 0, 0]}
                    barSize={60}
                    label={(props: any) => {
                      // props.value contains the bar value (percentage)
                      const percentage = props.value || 0;
                      if (percentage === 0) return null;
                      return (
                        <text
                          x={props.x + props.width / 2}
                          y={props.y - 5}
                          fill="#00518E"
                          textAnchor="middle"
                          fontSize={12}
                          fontWeight="bold"
                        >
                          {formatPercent(percentage)}
                        </text>
                      );
                    }}
                  />
                  {/* Web second (Red) */}
                  <Bar
                    dataKey="Web"
                    fill="#AA0827"
                    name="Web"
                    radius={[4, 4, 0, 0]}
                    barSize={60}
                    label={(props: any) => {
                      // props.value contains the bar value (percentage)
                      const percentage = props.value || 0;
                      if (percentage === 0) return null;
                      return (
                        <text
                          x={props.x + props.width / 2}
                          y={props.y - 5}
                          fill="#AA0827"
                          textAnchor="middle"
                          fontSize={12}
                          fontWeight="bold"
                        >
                          {formatPercent(percentage)}
                        </text>
                      );
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div
              className="text-center text-text-color py-8"
              role="status"
              aria-live="polite"
            >
              Nenhum dado disponível para o gráfico
            </div>
          )}
        </div>
      </BsdsCard>

      {/* Summary Table */}
      <div
        id={summarySectionId}
        className="bsds-mt-24"
        role="region"
        aria-label="Resumo de faturamento"
      >
        <div className="bsds-align-items-center bsds-mb-16">
          <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
            <BsdsIcon icon={iconDocGenericoPreco} className="w-4 h-4" aria-hidden="true" />
            <h6 className="bsds-ml-8 font-semibold">Resumo</h6>
          </div>
        </div>
        {/* Search and Sort */}
        <div className="bsds-mb-16 bsds-mt-16">
          <div
            className="flex flex-col sm:flex-row gap-4 w-full"
            role="group"
            aria-label="Ferramentas de busca e ordenação"
          >
            {/* Search field */}
            <div className="flex-1">
              <BsdsInput
                id={searchInputId}
                type="text"
                placeholder="Pesquisar por número do contrato..."
                value={searchTerm}
                onInput={(e) => {
                  const target = e.target as HTMLBsdsInputElement;
                  const value = (e as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
                  setSearchTerm(String(value));
                }}
                platform="web"
                aria-label="Pesquisar por número do contrato no resumo de faturamento"
                aria-describedby={`${searchInputId}-help`}
              />
              <span
                id={`${searchInputId}-help`}
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
              >
                Digite o número do contrato para pesquisar
              </span>
            </div>

            {/* Sort dropdown */}
            <div className="w-full sm:w-60">
              <Dropdown
                id="sort-summary"
                placeholder="Ordenar por..."
                value={sortBy}
                onChange={setSortBy}
                options={[
                  { label: 'Nome (A-Z)', value: 'name' },
                  { label: 'Nome (Z-A)', value: 'name-desc' },
                  { label: '% Pendente (Menor)', value: 'percentPending' },
                  { label: '% Pendente (Maior)', value: 'percentPending-desc' },
                  { label: '% Emitido (Menor)', value: 'percentIssued' },
                  { label: '% Emitido (Maior)', value: 'percentIssued-desc' },
                  { label: 'Valor Pendente (Menor)', value: 'valuePending' },
                  { label: 'Valor Pendente (Maior)', value: 'valuePending-desc' },
                  { label: 'Valor Emitido (Menor)', value: 'valueIssued' },
                  { label: 'Valor Emitido (Maior)', value: 'valueIssued-desc' },
                ]}
              />
            </div>
          </div>
        </div>

        <BsdsCard className="bsds-p-16">
          <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
            <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
            <h4 className="opacity-80">Resumo</h4>
          </div>
          <DashboardPaymentSummaryTable
            items={paginatedSummaryData}
            viewType={viewType}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
            onExport={handleExport}
            currentPage={uiCurrentPage}
            totalPages={totalPages}
            totalItems={!isLoading ? totalElements : 0}
            onPageChange={handlePageChange}
          />
        </BsdsCard>
      </div>
    </div>
  );
};

export default DashboardPaymentTab;